import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { setParentUnlocked, isParentUnlocked } from "@/lib/parentSession";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Lock, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";

/**
 * PinGate — wraps a parent-only screen.
 * - If parent has no PIN: prompt to set one
 * - If parent has PIN: prompt to verify
 * Calls onSuccess() once authorized.
 */
export const PinGate = ({ open, onOpenChange, onSuccess, title = "Parent zone", force = false }) => {
    const [hasPin, setHasPin] = useState(null);
    const [pin, setPin] = useState("");
    const [pin2, setPin2] = useState("");
    const [busy, setBusy] = useState(false);

    // Auto-bypass if already unlocked this session (unless force=true)
    useEffect(() => {
        if (open && !force && isParentUnlocked()) {
            onSuccess();
            onOpenChange(false);
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open) {
            setPin("");
            setPin2("");
            return;
        }
        api.get("/auth/pin/status").then((r) => setHasPin(r.data.has_pin)).catch(() => {});
    }, [open]);

    const submit = async () => {
        if (busy) return;
        if (!/^\d{4}$/.test(pin)) return toast.error("PIN must be 4 digits");
        setBusy(true);
        try {
            if (hasPin) {
                await api.post("/auth/pin/verify", { pin });
                setParentUnlocked(true);
                toast.success("Unlocked ✨");
                onSuccess();
            } else {
                if (pin !== pin2) {
                    toast.error("PINs do not match");
                    setBusy(false);
                    return;
                }
                await api.post("/auth/pin/set", { pin });
                setParentUnlocked(true);
                toast.success("PIN set");
                onSuccess();
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Wrong PIN");
        } finally {
            setBusy(false);
        }
    };

    const reset = async () => {
        if (!window.confirm("Reset your parent PIN? You'll set a new one.")) return;
        await api.post("/auth/pin/reset");
        setParentUnlocked(false);
        setHasPin(false);
        setPin("");
        setPin2("");
        toast.success("PIN cleared. Choose a new one.");
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#12182b] border border-[var(--bb-border)] text-white">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl flex items-center gap-2">
                        <Lock size={20} className="text-[#b388ff]" /> {title}
                    </DialogTitle>
                    <DialogDescription className="text-[#b0b8d6]">
                        {hasPin === null
                            ? "Checking…"
                            : hasPin
                              ? "Enter your 4-digit parent PIN."
                              : "Set a 4-digit PIN to lock the parent zone."}
                    </DialogDescription>
                </DialogHeader>
                {hasPin !== null && (
                    <div className="space-y-4 py-2">
                        <PinInput value={pin} onChange={setPin} testid="pin-input" autoFocus />
                        {!hasPin && (
                            <PinInput
                                value={pin2}
                                onChange={setPin2}
                                placeholder="Confirm PIN"
                                testid="pin-confirm"
                            />
                        )}
                        <div className="flex justify-between gap-2">
                            <button
                                onClick={reset}
                                className="bb-btn-ghost !text-xs flex items-center gap-1"
                                data-testid="pin-reset"
                            >
                                <RotateCcw size={12} /> Forgot PIN
                            </button>
                            <button
                                onClick={submit}
                                disabled={busy}
                                className="bb-btn-primary flex items-center gap-2"
                                data-testid="pin-submit"
                            >
                                <Sparkles size={14} />{" "}
                                {hasPin ? "Unlock" : "Set PIN & enter"}
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

const PinInput = ({ value, onChange, placeholder = "PIN", testid, autoFocus }) => (
    <input
        type="password"
        inputMode="numeric"
        maxLength={4}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 4))}
        placeholder={placeholder}
        className="w-full bg-[#080b14] border border-[#1f2942] rounded-xl px-4 py-3 text-2xl tracking-[1em] text-center font-mono"
        data-testid={testid}
    />
);

export default PinGate;
